import type { de } from './de';

export const en: typeof de = {
  brand: {
    name: 'PRIVATUM',
    tagline: 'Confidential by design.',
    taglineAlt1: 'Professional secrecy deserves professional infrastructure.',
    taglineAlt2: 'Your secrets were never ours.',
  },
  nav: {
    home: 'Home',
    features: 'Features',
    security: 'Security',
    pricing: 'Pricing',
    verticals: 'Industries',
    login: 'Sign in',
    getStarted: 'Get started',
    dashboard: 'Dashboard',
  },
  hero: {
    title: 'Your clients trust you with their most sensitive information.',
    titleHighlight: "Don't make them send it by email.",
    subtitle: 'Privatum gives lawyers, physicians and trusted professionals a secure, zero-knowledge channel for receiving confidential documents.',
    cta: 'Start securely',
    ctaSecondary: 'See how it works',
  },
  problem: {
    title: 'The problem with email attachments',
    subtitle: 'Every copy is an attack surface. Fewer copies, less exposure, more control.',
    emailPath: 'Client → Email provider → Mail server → Inbox → Device → Backup → DMS',
    privatumPath: 'Client → Encrypted on device → Privatum → Authorized professional',
    emailLabel: 'Today: 7 copies, 7 risks',
    privatumLabel: 'With Privatum: encrypted, direct, controlled',
  },
  howItWorks: {
    title: 'As easy as email. Safer than anything else.',
    step1Title: 'Create your secure channel',
    step1Desc: 'Sign up in 90 seconds. Get your personal Secure Inbox at privatum.ch/your-name.',
    step2Title: 'Your client uploads',
    step2Desc: 'No account, no app, no technical knowledge required. Files are encrypted on the sender\'s device.',
    step3Title: 'Receive confidentially',
    step3Desc: 'Only you can decrypt the documents. Privatum cannot read them. Nobody else can.',
  },
  upload: {
    title: 'SECURE DOCUMENT TRANSFER',
    sendingTo: 'You are securely sending documents to:',
    dragDrop: 'Drag your files here',
    chooseFiles: 'Choose files',
    yourName: 'Your name (optional)',
    yourEmail: 'Your email (optional)',
    message: 'Message (optional)',
    encryption: 'Your files are encrypted on your device before they are uploaded. Privatum cannot read them.',
    submit: 'Encrypt & Send Securely',
    encrypting: 'Encrypting locally …',
    uploading: 'Uploading encrypted data …',
    delivered: 'Securely delivered.',
    plgLine: 'Do you receive confidential documents professionally? Create your own secure Privatum channel.',
    accessCode: 'Access code',
    accessCodeHint: 'Please enter the code you received from your professional.',
  },
  pricing: {
    title: 'Simple, transparent pricing',
    subtitle: 'Clients are always free and unlimited.',
    monthly: '/month',
    annual: 'Annual billing: 2 months free',
    solo: {
      name: 'SOLO',
      price: 'CHF 19',
      desc: 'For solo practitioners',
      features: [
        'Personal Secure Inbox',
        'Unlimited Secure Links',
        '20 GB encrypted temporary storage',
        'Automatic deletion',
        'Professional profile',
        'Basic activity log',
      ],
    },
    professional: {
      name: 'PROFESSIONAL',
      price: 'CHF 49',
      desc: 'For small firms and practices',
      popular: 'Most popular',
      features: [
        'Up to 5 users',
        'Team inbox',
        '100 GB encrypted storage',
        'Custom branding',
        'Advanced retention rules',
        'Team permissions',
        'Website widget',
      ],
    },
    practice: {
      name: 'PRACTICE',
      price: 'CHF 149',
      desc: 'For larger firms and practices',
      features: [
        'Up to 20 users',
        '500 GB encrypted storage',
        'Roles & permissions',
        'API access',
        'DMS integrations',
        'Advanced audit logs',
        'Priority support',
      ],
    },
    enterprise: {
      name: 'ENTERPRISE',
      price: 'Custom',
      desc: 'For large organizations',
      features: [
        'Private relay infrastructure',
        'Dedicated Swiss storage',
        'Customer-controlled storage',
        'SSO integration',
        'Custom retention',
        'On-premise option',
        'SLA & compliance support',
      ],
    },
    cta: 'Get started',
    ctaEnterprise: 'Contact us',
  },
  verticals: {
    title: 'For every trusted profession',
    lawyers: 'Lawyers',
    healthcare: 'Healthcare',
    psychotherapy: 'Psychotherapy',
    fiduciary: 'Fiduciary & Tax',
    notaries: 'Notaries',
    wealth: 'Wealth Management',
    familyOffice: 'Family Offices',
  },
  trust: {
    title: 'Trust through architecture',
    subtitle: 'We don\'t make exaggerated promises. We build verifiable guarantees.',
    zeroKnowledge: 'Zero-knowledge architecture',
    zeroKnowledgeDesc: 'Documents are encrypted before reaching our infrastructure. We cannot read them.',
    clientSide: 'Client-side encryption',
    clientSideDesc: 'All cryptographic operations happen in your browser. Keys never leave your device.',
    swissHosting: 'Swiss data hosting',
    swissHostingDesc: 'Encrypted data is stored in Switzerland, under Swiss data protection law.',
    dataMin: 'Data minimization',
    dataMinDesc: 'We store as little information as necessary, for as little time as necessary.',
    openStandards: 'Open cryptographic standards',
    openStandardsDesc: 'No proprietary cryptography. Verifiable, auditable, transparent.',
    noAds: 'No tracking, no advertising',
    noAdsDesc: 'No analytics, no pixels, no data monetization. Ever.',
  },
  dashboard: {
    greeting: 'Good morning',
    inbox: 'Inbox',
    documents: 'Documents',
    secureLinks: 'Secure Links',
    contacts: 'Contacts',
    activity: 'Activity',
    settings: 'Settings',
    newDeliveries: 'New deliveries',
    activeLinks: 'Active links',
    storage: 'Storage',
    securityStatus: 'Security status',
    securityCenter: 'Security Center',
    privacyDashboard: 'Privacy Dashboard',
    recentActivity: 'Recent activity',
    createSecureLink: 'Create Secure Link',
    viewAll: 'View all',
    noItems: 'No items found.',
    logout: 'Sign out',
    secureInbox: 'Your Secure Inbox',
    copy: 'Copy',
    new: 'New',
    viewed: 'Viewed',
    downloaded: 'Downloaded',
    document: 'Document',
    documentsPlural: 'Documents',
  },
  secureLink: {
    title: 'Create Secure Link',
    recipientName: 'Recipient name',
    recipientEmail: 'Recipient email (optional)',
    purpose: 'Purpose',
    expiration: 'Expiration',
    maxUploads: 'Maximum uploads',
    maxFileSize: 'Maximum file size',
    accessCode: 'Access code',
    accessCodeDefault: 'Default: enabled (recommended)',
    accessCodeWarning: 'Without an access code, anyone with the link can upload files. The access code ensures only the intended person has access.',
    oneTimeUse: 'One-time use',
    requireIdentification: 'Require sender identification',
    deleteAfterDownload: 'Delete after download',
    deleteAfterDays: 'Delete after N days',
    allowReplies: 'Allow secure replies',
    create: 'Create link',
    copyLink: 'Copy link',
    showQR: 'Show QR code',
    sendEmail: 'Send via email',
    embedSnippet: 'Embed snippet',
  },
  security: {
    title: 'Security Center',
    authEnabled: 'Strong authentication enabled',
    recoveryConfigured: 'Recovery configured',
    trustedDevices: 'Trusted devices reviewed',
    retentionSet: 'Default retention policy set',
    identityVerified: 'Professional identity verified',
    configure: 'Configure',
    configured: 'Configured',
  },
  recovery: {
    title: 'Recovery',
    subtitle: 'Ensure you never lose access to your encrypted data.',
    disclaimer: 'Recovery never grants Privatum unilateral access to your document contents.',
    encryptedPackage: 'Encrypted recovery package',
    trustedDevice: 'Trusted device',
    organizational: 'Organizational recovery',
    shamir: 'Shamir Secret Sharing',
    trustedContacts: 'Trusted contacts',
  },
  onboarding: {
    title: 'Your secure channel is being created',
    handle: 'Your Privatum handle',
    handleHint: 'Your permanent address: privatum.ch/',
    name: 'Full name',
    professionalTitle: 'Professional title',
    jurisdiction: 'Jurisdiction',
    photo: 'Photo / Logo (optional)',
    retention: 'Default retention',
    continue: 'Continue',
    complete: 'Create channel',
    recoveryPrompt: 'Would you like to set up recovery now?',
    recoveryLater: 'Set up later',
    recoveryNow: 'Set up now',
  },
  footer: {
    tagline: 'Confidential by design.',
    security: 'Security & Trust',
    legal: 'Legal',
    privacy: 'Privacy',
    terms: 'Terms',
    imprint: 'Imprint',
    contact: 'Contact',
    vibed: 'Vibed with Shakespeare',
  },
  vault: {
    title: 'Vault',
    newFolder: 'New folder',
    documents: 'Documents',
    permissions: 'Permissions',
    allEncrypted: 'All data is encrypted client-side.',
    encrypted: 'encrypted',
    folders: 'Folders',
    searchPlaceholder: 'Search …',
    resultsFor: 'result(s) for',
    noDocuments: 'No documents found.',
    folderEmpty: 'This folder is empty. Move documents from the inbox here.',
    shareAccess: 'Share access',
    rename: 'Rename',
    secureDelete: 'Delete securely',
    decrypting: 'Decrypting document',
    downloading: 'Downloading',
    deleting: 'is being securely deleted.',
    folderCreated: 'Folder created.',
    folderDeleted: 'Folder is being deleted.',
    createFolder: 'Create folder',
    newVaultFolder: 'New Vault folder',
    folderName: 'Name',
    folderNamePlaceholder: 'Estate case Mueller',
    folderDescription: 'Description (optional)',
    folderDescPlaceholder: 'Inheritance matters for the Mueller family',
    grantedPermissions: 'Granted access permissions',
    grantedPermissionsDesc: 'Cryptographically signed access grants. Revocation is real: keys are rotated, permissions expire.',
    permissionLevels: 'Permission levels',
    permView: 'View',
    permViewDesc: 'Recipient can decrypt and view the document in the browser.',
    permDownload: 'Download',
    permDownloadDesc: 'Recipient can save the decrypted document locally.',
    permForward: 'Forward',
    permForwardDesc: 'Recipient can transfer encrypted access to third parties.',
    permDelegate: 'Delegate',
    permDelegateDesc: 'Recipient can grant permissions to others.',
    uploadFile: 'Upload file',
    uploadTitle: 'Encrypt & store file',
    uploadDragDrop: 'Drag files here or click',
    uploadSelectFiles: 'Select files',
    uploadEncrypting: 'Encrypting …',
    uploadStoring: 'Storing …',
    uploadComplete: 'Encrypted & stored.',
    retentionPolicy: 'Retention',
    retentionManual: 'Delete manually',
    retentionAfterDownload: 'Delete after download',
    retention24h: 'After 24 hours',
    retention7d: 'After 7 days',
    retention30d: 'After 30 days',
    retention90d: 'After 90 days',
    retention1y: 'After 1 year',
    openVault: 'Open Vault',
    encryptedDocuments: 'Encrypted documents',
    foldersMatters: 'Folders / Matters',
    activePermissions: 'Active permissions',
    vaultDesc: 'Vault — Encrypted document storage',
    vaultSubDesc: 'Matters, cases, and folders with client-side encryption. Cryptographic access permissions with revocation.',
    revokeAccess: 'Revoke access',
    revokedFor: 'Access for',
    revoked: 'revoked.',
    deletion: 'Deletion',
    accessTo: 'Access to',
    grantedAt: 'Granted',
    expiresAt: 'Expires',
    active: 'Active',
    expired: 'Expired',
    revokedStatus: 'Revoked',
    inbox: 'Inbox',
    manualUpload: 'Manual upload',
    secureLink: 'Secure link',
    pendingDeletion: 'Deletion',
  },
  audit: {
    title: 'Audit Log',
    subtitle: 'Cryptographically chained integrity proofs. Each entry references the previous hash.',
    chainValid: 'Integrity chain valid',
    chainInvalid: 'Integrity chain broken at entry',
    verifyChain: 'Verify chain',
    verifying: 'Verifying …',
    verified: 'Verified',
    entries: 'Entries',
    documentHash: 'Document hash',
    entryHash: 'Entry hash',
    previousHash: 'Previous hash',
    direction: 'Direction',
    inbound: 'Inbound',
    outbound: 'Outbound',
    channel: 'Channel',
    noEntries: 'No integrity entries yet.',
    noEntriesDesc: 'Upload files to the Vault or receive documents — every action is recorded in the integrity chain.',
    eventEncrypt: 'Encrypted & stored',
    eventUpload: 'Uploaded',
    eventDownload: 'Downloaded',
    eventDelete: 'Deleted',
    eventMove: 'Moved to Vault',
    eventDelivery: 'Delivery received',
    genesis: 'Genesis',
    timestamp: 'Timestamp',
    filename: 'Filename',
    fileSize: 'File size',
    copyHash: 'Copy hash',
    allChannels: 'All channels',
    filterByChannel: 'Filter by channel',
    privacyEncryptedStorage: 'Encrypted storage',
    privacyUsed: 'Used',
    privacyScheduledDeletions: 'Scheduled deletions',
    privacyScheduledDeletionsDesc: 'Documents that will be automatically deleted.',
    privacyExpiredLinks: 'Expired links',
    privacyMetadataRetained: 'Retained metadata',
    privacyMetadataRetainedDesc: 'Privatum stores only minimal metadata. Document contents are never included.',
    privacyDataMinStatement: 'Privatum is designed to store as little information as necessary, for as little time as necessary.',
    privacyDataMinSubtext: 'Document contents are encrypted before reaching our infrastructure. We cannot read them.',
    privacyPanicDelete: 'Emergency deletion',
    privacyPanicDeleteDesc: 'Permanently deletes all encrypted documents and metadata. This action cannot be undone.',
    privacyPanicDeleteBtn: 'Permanently delete all data',
    privacyPanicConfirmTitle: 'Confirm emergency deletion',
    privacyPanicConfirmDesc: 'This action permanently deletes all encrypted documents, metadata, Secure Links, and activity logs.',
    privacyPanicConfirmPrompt: 'Type DELETE to confirm:',
    privacyPanicConfirmWord: 'DELETE',
    privacyPanicConfirmBtn: 'Permanently delete',
  },
  common: {
    loading: 'Loading …',
    error: 'An error occurred.',
    retry: 'Try again',
    cancel: 'Cancel',
    save: 'Save',
    delete: 'Delete',
    back: 'Back',
    next: 'Next',
    close: 'Close',
    download: 'Download',
    copy: 'Copy',
    copied: 'Copied',
    search: 'Search',
  },
} as const;
